import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoonIcon, SunIcon, Menu, ChevronDown, Layers, Eye, Instagram, MessageSquare, Music, Facebook, Twitter, ArrowRight, Hash, FileText, LetterText, EyeOff, Type, Linkedin, Youtube } from 'lucide-react';
import { DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';
import { Link } from 'react-router-dom';
import { PrefetchLink } from '@/components/PrefetchLink';
import logoLight from '@/assets/logo-light.png';
import logoDark from '@/assets/logo-dark.png';

export function Header() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toolsLinks = [
    { href: '/tools/word-counter', label: 'Word Counter', icon: FileText },
    { href: '/tools/hashtag-generator', label: 'Hashtag Generator', icon: Hash },
    { href: '/tools/social-preview', label: 'Social Preview', icon: Eye },
    { href: '/tools/batch-transform', label: 'Batch Transform', icon: Layers },
    { href: '/tools/case-converter', label: 'Case Converter', icon: LetterText },
    { href: '/tools/invisible-text', label: 'Invisible Text', icon: EyeOff },
    { href: '/tools/unicode-lookup', label: 'Unicode Lookup', icon: Type },
  ];

  const platformLinks = [
    { href: '/instagram-fonts', label: 'Instagram', icon: Instagram },
    { href: '/tiktok-fonts', label: 'TikTok', icon: Music },
    { href: '/discord-fonts', label: 'Discord', icon: MessageSquare },
    { href: '/twitter-fonts', label: 'Twitter/X', icon: Twitter },
    { href: '/facebook-fonts', label: 'Facebook', icon: Facebook },
    { href: '/linkedin-fonts', label: 'LinkedIn', icon: Linkedin },
    { href: '/youtube-fonts', label: 'YouTube', icon: Youtube },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src={resolvedTheme === 'dark' ? logoDark : logoLight} 
              alt="Text.onl" 
              className="h-8"
            />
          </Link>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          <Link to="/">
            <Button variant="ghost" size="sm">
              Home
            </Button>
          </Link>
          <PrefetchLink to="/styles">
            <Button variant="ghost" size="sm">
              All Styles
            </Button>
          </PrefetchLink>
          {/* Platforms Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1">
                Platforms
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {platformLinks.map((platform) => (
                <DropdownMenuItem key={platform.href} asChild>
                  <PrefetchLink to={platform.href} className="flex items-center gap-2 cursor-pointer">
                    <platform.icon size={16} />
                    {platform.label}
                  </PrefetchLink>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <PrefetchLink to="/platforms" className="flex items-center gap-2 cursor-pointer text-primary font-medium">
                  <ArrowRight size={16} />
                  View All Platforms
                </PrefetchLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <PrefetchLink to="/custom-style">
            <Button variant="ghost" size="sm">
              Custom Style
            </Button>
          </PrefetchLink>

          {/* Tools Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1">
                Tools
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {toolsLinks.map((tool) => (
                <DropdownMenuItem key={tool.href} asChild>
                  <PrefetchLink to={tool.href} className="flex items-center gap-2 cursor-pointer">
                    <tool.icon size={16} />
                    {tool.label}
                  </PrefetchLink>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <PrefetchLink to="/tools" className="flex items-center gap-2 cursor-pointer text-primary font-medium">
                  <ArrowRight size={16} />
                  View All Tools
                </PrefetchLink>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Theme toggle and mobile menu */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleTheme}
            className="h-8 w-8 p-0"
            aria-label="Toggle theme"
          >
            {resolvedTheme === 'dark' ? (
              <SunIcon className="h-4 w-4" />
            ) : (
              <MoonIcon className="h-4 w-4" />
            )}
          </Button>

          {/* Mobile menu */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="md:hidden h-8 w-8 p-0"
                aria-label="Open menu"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80">
              <div className="flex flex-col space-y-1 mt-8">
                <Link
                  to="/"
                  className="flex items-center px-3 py-2 text-lg font-medium rounded-md hover:bg-accent transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <PrefetchLink
                  to="/styles"
                  className="flex items-center px-3 py-2 text-lg font-medium rounded-md hover:bg-accent transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  All Styles
                </PrefetchLink>
                <PrefetchLink
                  to="/custom-style"
                  className="flex items-center px-3 py-2 text-lg font-medium rounded-md hover:bg-accent transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Custom Style
                </PrefetchLink>

                {/* Platforms Section - Collapsible */}
                <Collapsible className="pt-4">
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent rounded-md transition-colors">
                    <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      Platforms
                    </span>
                    <ChevronDown size={16} className="text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 mt-1">
                    {platformLinks.map((platform) => (
                      <PrefetchLink
                        key={platform.href}
                        to={platform.href}
                        className="flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md hover:bg-accent transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <platform.icon size={18} className="text-muted-foreground" />
                        {platform.label}
                      </PrefetchLink>
                    ))}
                    <PrefetchLink
                      to="/platforms"
                      className="flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md hover:bg-accent transition-colors text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ArrowRight size={18} />
                      View All Platforms
                    </PrefetchLink>
                  </CollapsibleContent>
                </Collapsible>

                {/* Tools Section - Collapsible */}
                <Collapsible className="pt-2">
                  <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 hover:bg-accent rounded-md transition-colors">
                    <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                      Tools
                    </span>
                    <ChevronDown size={16} className="text-muted-foreground" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 mt-1">
                    {toolsLinks.map((tool) => (
                      <PrefetchLink
                        key={tool.href}
                        to={tool.href}
                        className="flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md hover:bg-accent transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <tool.icon size={18} className="text-muted-foreground" />
                        {tool.label}
                      </PrefetchLink>
                    ))}
                    <PrefetchLink
                      to="/tools"
                      className="flex items-center gap-3 px-3 py-2 text-base font-medium rounded-md hover:bg-accent transition-colors text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <ArrowRight size={18} />
                      View All Tools
                    </PrefetchLink>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
